import { Button, Spin, Table, Tooltip, message } from 'antd';
import { pick } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useState } from 'react';

import Icon, { ExclamationCircleOutlined } from '@ant-design/icons';
import InfoIcon from '@icons/emp/info.svg';
import CloseIcon from '@icons/event-management/close-blue.svg';
import HyperLinkIcon from '@icons/event-management/hyperlink.svg';
import { usePostFunnel } from '@src/emp-3.0/api/queryHooks';
import { IPathConversion } from '@src/emp-3.0/components/Sankey/PathConversion/typed';
import {
  isDropOffOrOthersNode,
  isOtherNode,
  transformFunnelDataToTableData,
} from '@src/emp-3.0/components/Sankey/utils';
import FunnelBarChart from '@src/emp-3.0/components/common/FunnelBarChart';
import { IFunnel, IFunnelPayload } from '@src/emp-3.0/types';
import { trackViewInFunnelClick } from '@src/sensor';

import { Empty, Header } from './styled';
import useToFunnelStore from '../store/toFunnelStore';

const PathConversion: React.FC<IPathConversion> = ({
  sankeyData = { direction: 'forward', nodes: [], links: [] },
  filters = [],
  interval = '',
  selectedNodes = [],
  onClearPath,
  onConvertToFunnel,
  selectedNodeInstances = [],
  mask,
}) => {
  const { setInitFunnelPayload } = useToFunnelStore();
  const { direction } = sankeyData;
  const isForwardDirection = direction === 'forward';
  const enableRenderBarChart =
    isForwardDirection &&
    selectedNodes?.length > 0 &&
    selectedNodes.every((node) => !isOtherNode(node));

  const { mutate: getFunnelData, isLoading } = usePostFunnel();

  const [funnelRes, setFunnelRes] = useState<{
    event_names: string[];
    funnel_data: IFunnel[];
  }>({ event_names: [], funnel_data: [] });

  const funnelEvents = useMemo(
    () =>
      selectedNodes.map(({ event_name, filters }) => ({
        event_name,
        filters,
      })),
    [selectedNodes],
  );

  const { dataSource, columns } = useMemo(
    () => transformFunnelDataToTableData(funnelRes),
    [funnelRes],
  );

  useEffect(() => {
    if (interval === '' || !enableRenderBarChart) return;
    const payload: IFunnelPayload = {
      interval,
      filters,
      funnel_events: funnelEvents,
      dimension_breakdowns: [],
    };

    getFunnelData(
      {
        src: 'trends',
        is_in_sankey: 1,
        payload,
      },
      {
        onSuccess: (data) => {
          setFunnelRes(data?.data);
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
        },
      },
    );
  }, [filters, funnelEvents, getFunnelData, interval]);

  const handleToFunnel = () => {
    const gFilters = filters.map((item) => {
      return item?.map((x) => {
        return {
          field: x.field,
          op: x.op,
          value: x.value.join(','),
        };
      });
    });
    const payload = {
      interval,
      filters: gFilters,
      funnelEvents: selectedNodeInstances.map((x) => {
        return {
          event_name: x.event_name,
          filters: x.filters,
        };
      }),
    };
    trackViewInFunnelClick({ ...payload });
    setInitFunnelPayload(payload);
    onConvertToFunnel?.(payload);
  };

  return (
    <Spin spinning={isLoading}>
      <div style={{ marginBottom: 32 }}>
        <Header>
          <span style={{ flex: 1, padding: '4px 8px', alignItems: 'center' }}>
            Conversion Funnel{' '}
            <Tooltip
              placement="bottomLeft"
              title={'The conversion aggregates events in different layers'}
            >
              <ExclamationCircleOutlined style={{ fontSize: '12px' }} />
            </Tooltip>
          </span>
          {enableRenderBarChart && (
            <>
              <Button
                type="primary"
                ghost
                style={{ background: '#fff' }}
                onClick={handleToFunnel}
                disabled={selectedNodes.some((node) => isDropOffOrOthersNode(node)) || mask}
              >
                View Funnel
                <Icon component={HyperLinkIcon} />
              </Button>

              <Button
                style={{ background: '#000', color: '#fff' }}
                onClick={onClearPath}
                disabled={mask}
              >
                <Icon component={CloseIcon} style={{ color: '#fff' }} />
                Clear Path
              </Button>
            </>
          )}
        </Header>
        {enableRenderBarChart ? (
          <>
            <FunnelBarChart type="sankey" funnelRes={funnelRes} selectedNodes={selectedNodes} />
            <Table size="small" columns={columns} dataSource={dataSource} pagination={false} />
          </>
        ) : (
          <Empty>
            <Icon component={InfoIcon} style={{ fontSize: '14px' }} />
            <span>
              {direction === 'forward'
                ? 'Select a series of nodes to view conversions'
                : 'Upcoming: Path conversion for Sankey with an ending node'}
            </span>
          </Empty>
        )}
      </div>
    </Spin>
  );
};

export default React.memo(PathConversion, (prevProps, nextProps) => {
  return isEqual(
    pick(prevProps, [
      'sankeyData',
      'filters',
      'interval',
      'selectedNodes',
      'selectedNodeInstances',
    ]),
    pick(nextProps, [
      'sankeyData',
      'filters',
      'interval',
      'selectedNodes',
      'selectedNodeInstances',
    ]),
  );
});
